import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Shield } from 'lucide-react'

import { useToast } from '~/design-system/atom/use-toast'
import { deleteOrgProjectWebhook } from '~/generated/client'
import {
  useEventWebhookUrlQuery,
  useSetEventWebhookURL,
} from '~/hooks/query/settings'
import { validateURL } from '~/utils/urlUtils'
import { AdminWebhookSection } from './components/AdminWebhook'
import { ApprovalPopup } from './settings'

export const EventWebHookAndSettingBlock = () => {
  const { toast } = useToast()
  const { data, isLoading, refetch } = useEventWebhookUrlQuery('event')
  const { url } = data ?? {}
  const [inputCallbackURL, setInputCallbackURL] = useState('')
  const [stagedCallbackURL, setStagedCallbackURL] = useState<string | null>(
    null,
  )
  const [isEditing, setIsEditing] = useState(false)

  const event_webhook_url =
    url?.type === 'event' ? url.event_webhook_url : undefined

  useEffect(() => {
    if (event_webhook_url) {
      setInputCallbackURL(event_webhook_url)
    }
  }, [event_webhook_url, isLoading])

  const { mutate: setEventWebHookURLMutation } = useSetEventWebhookURL()

  const { mutate: deleteEventWebHookURLMutation } = useMutation({
    mutationFn: () => {
      return deleteOrgProjectWebhook({
        requestBody: {
          type: 'event',
        },
      })
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Event webhook URL was deleted successfully',
      })
      refetch()
      setStagedCallbackURL(null)
      setIsEditing(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to delete event webhook URL',
      })
      console.error(error)
    },
  })

  const handleUpdateTriggerURL = (callbackFn?: () => void) => {
    if (stagedCallbackURL !== null) {
      if (stagedCallbackURL === '') {
        deleteEventWebHookURLMutation()
      } else {
        setEventWebHookURLMutation(
          {
            requestBody: {
              webhook_url: stagedCallbackURL,
              type: 'event',
            },
          },
          {
            onSuccess: () => {
              if (stagedCallbackURL === '') {
                toast({
                  title: 'Success',
                  description: 'Event webhook URL was deleted successfully',
                  variant: 'success',
                })
              } else {
                toast({
                  title: 'Success',
                  description: 'Event webhook URL was updated successfully',
                  variant: 'success',
                })
              }
              setStagedCallbackURL(null)
              setIsEditing(false)
              setInputCallbackURL(stagedCallbackURL)
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              callbackFn && callbackFn()
            },
          },
        )
      }
    }
  }

  if (isLoading) {
    return (
      <div className="m-auto flex w-[900px] flex-col items-start">
        <div role="status" className="w-full animate-pulse">
          <div className="mb-4 h-2.5 w-48 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 max-w-[330px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 max-w-[300px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="m-auto flex w-[900px] flex-col items-start">
      {stagedCallbackURL !== null && (
        <ApprovalPopup
          heading={`Are you sure about ${stagedCallbackURL === '' ? 'removing' : 'updating'} the webhook URL?`}
          description="This can potentially break integrations that rely on this webhook URL."
          approveButtonText={
            stagedCallbackURL === ''
              ? 'Proceed with delete'
              : 'Proceed with update'
          }
          onApprove={async () => {
            handleUpdateTriggerURL()
          }}
          setOpen={() => {
            setStagedCallbackURL(null)
          }}
        />
      )}
      <AdminWebhookSection
        icon={<Shield className="h-5 w-5" />}
        title="Admin Event Webhook URL"
        description="Get notification on changes in your Composio account."
        webhookUrl={inputCallbackURL}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        onDelete={() => {
          setStagedCallbackURL('')
        }}
        onSave={(url) => {
          if (!validateURL(url)) {
            toast({
              title: 'Error',
              description: 'Please enter a valid event webhook URL',
              variant: 'destructive',
            })
            return
          }
          setStagedCallbackURL(url)
        }}
        onChange={(url) => {
          setInputCallbackURL(url)
        }}
      />
    </div>
  )
}
